import { vec3 } from 'gl-matrix'
import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'

import { RenderContext } from '@/webgl/core/RenderContext'
import Programs from '@/webgl/glsl/programs'
import { clamp01, mix, randomFloat } from '@/webgl/math'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import GltfParticles, { ParticleData } from '@/webgl/runner/rendering/GltfParticles'

const PCOUNT = 15

export type ParticlesResource = {
  request: string
  id: string
  scale?: number
}

export default class ParticlesRenderer implements ILoadable {
  node: Node = new Node()

  time: number = 0
  resources: ParticlesResource[] = []
  renderers: Record<string, GltfParticles> = {}
  loaded: boolean = false

  private activeVariant: string = 'default'

  constructor(
    readonly gl: GLContext,
    readonly world: GameWorld
  ) {
    this.node.z = -8
    this.world.root.add(this.node)
  }

  setResources(resources: ParticlesResource[]) {
    this.resources = resources
  }

  setActiveVariant(id: string) {
    this.activeVariant = id
  }

  async load(abort: AbortSignal): Promise<void> {
    const program = Programs(this.gl).get('particles-std')

    for (const resource of this.resources) {
      const globalScale = resource.scale ?? 1

      const particles = new GltfParticles(this.gl, resource.request)
      await particles.load(abort)

      particles.program = program
      particles.cfg
        .enableBlend()
        .blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA)
        .enableCullface(true)
        .enableDepthTest(false)

      const data: ParticleData[] = []

      for (let i = 0; i < PCOUNT; i++) {
        const angle = randomFloat(0.3, Math.PI - 0.3)
        const x = Math.cos(angle) * 1.0
        const y = Math.sin(angle) * 0.8

        const position = vec3.fromValues(x, y, -Math.random())
        const rotation = Math.random() * Math.PI * 2

        const lifetime = 3.0
        const delay = Math.random() * 15.0

        const velocity = vec3.fromValues(x * 0.8, y * 0.3, 0.6)
        vec3.normalize(velocity, velocity)

        const speed = 1.0 + Math.random() * 1.2
        const scale = (Math.random() * 0.2 + 0.2) * globalScale
        const angularSpeed = Math.random() * 0.2 + 0.05

        data.push({
          position,
          velocity,
          rotation,
          scale,
          speed,
          angularSpeed,
          lifetime,
          delay,
        })
      }

      particles.generate(data)

      this.node.add(particles.node)
      this.renderers[resource.id] = particles
    }

    this.loaded = this.resources.length > 0
  }

  preRender(time: TimeEntity): void {
    const dt = mix(time.unscaledDeltaTime, time.deltaTime, this.world.state.value.jauge)
    const settings = this.world.settings
    this.time += dt * settings.scrollSpeed
    this.node.x += (this.world.player.node.x - this.node.x) * dt * 1.5
  }

  render(context: RenderContext): void {
    if (!this.loaded) return
    const particles = this.renderers[this.activeVariant]
    const settings = this.world.settings
    const prg = particles.program
    prg.use()
    prg.uTime(this.time)
    prg.uGlobalScale(clamp01(settings.scrollSpeed))

    particles.render(context)
  }
}
